import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  ErrorState,
  Input,
  Pagination,
  Select,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef, SelectOption } from '@react-apps/design-system';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';
import {
  DEFAULT_STORED_FILE_FILTERS,
  STORED_FILE_STATUS_LABELS,
  type FilesOwnerTypeSummaryRow,
  type FilesSummary,
  type FilesSummaryRow,
  type StoredFile,
  type StoredFileFilters,
} from '../../domain/entities/stored-file.entity';
import { formatBytes, formatDateTime } from '../format';
import { useFilesSummary, useStoredFiles } from '../hooks/use-stored-files';

const PER_PAGE = 20;

const STATUS_OPTIONS: SelectOption[] = [
  ...Object.entries(STORED_FILE_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
  { value: 'all', label: 'Todos' },
];

/**
 * Opções de bucket, módulo, BU e tipo de dono saem do summary, e não de uma
 * lista copiada do back. É um summary próprio, sem filtro nenhum: o da tela vem
 * filtrado, e nele um módulo só com arquivos apagados (ou outro módulo, com um
 * já escolhido) não viraria opção. O valor escolhido fica nas opções mesmo quando
 * nenhum arquivo o tem.
 */
const OPTIONS_FILTERS: StoredFileFilters = {
  ...DEFAULT_STORED_FILE_FILTERS,
  status: 'all',
};

function optionsFrom(
  rows: FilesSummaryRow[] | undefined,
  selected: string,
): SelectOption[] {
  const options = (rows ?? [])
    .filter((row): row is FilesSummaryRow & { key: string } => row.key !== null)
    .map((row) => ({ value: row.key, label: row.label }));
  if (selected && !options.some((o) => o.value === selected)) {
    options.push({ value: selected, label: selected });
  }
  return [{ value: '', label: 'Todos' }, ...options];
}

/**
 * O tipo de dono se escolhe como par `módulo · tipo`, porque o back só filtra o
 * dono com os dois juntos. Era texto livre, e quem digitava o módulo no lugar
 * do tipo recebia lista vazia sem saber por quê (2026-10-23). Com um módulo
 * escolhido, só os pares dele.
 */
function ownerTypeOptionsFrom(
  rows: FilesOwnerTypeSummaryRow[] | undefined,
  module: string,
  ownerType: string,
): SelectOption[] {
  const options = (rows ?? [])
    .filter((row) => module === '' || row.module === module)
    .map((row) => ({ value: row.key, label: row.label }));
  const selected = ownerKey(module, ownerType);
  if (ownerType && !options.some((o) => o.value === selected)) {
    options.push({ value: selected, label: `${module} · ${ownerType}` });
  }
  return [{ value: '', label: 'Todos' }, ...options];
}

function ownerKey(module: string, ownerType: string): string {
  return ownerType ? `${module}/${ownerType}` : '';
}

/** Filtros que moram no "Mais filtros". Com um deles na URL, ele abre aberto. */
const ADVANCED_FILTERS = [
  'bucket',
  'tenant',
  'minSizeMib',
  'maxSizeMib',
  'notResolvedSince',
] as const satisfies readonly (keyof StoredFileFilters)[];

function hasAdvanced(filters: StoredFileFilters): boolean {
  return ADVANCED_FILTERS.some((key) => filters[key] !== '');
}

function isDefault(filters: StoredFileFilters): boolean {
  return (
    Object.keys(DEFAULT_STORED_FILE_FILTERS) as (keyof StoredFileFilters)[]
  ).every((key) => filters[key] === DEFAULT_STORED_FILE_FILTERS[key]);
}

export function StoredFilesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Filtro novo volta à página 1 no mesmo `setSearchParams`: dois setters no
  // mesmo tick partem do mesmo `location.search` e o segundo apaga o primeiro.
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, DEFAULT_STORED_FILE_FILTERS);
  const setPage = (next: number) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  const setFilters = (next: StoredFileFilters) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, DEFAULT_STORED_FILE_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );

  const list = useStoredFiles(page, PER_PAGE, filters);
  const summary = useFilesSummary(filters);
  const options = useFilesSummary(OPTIONS_FILTERS).data;
  // Com `keepPreviousData` o dado do filtro anterior fica na tela até o novo
  // chegar: conta como carregando, para não passar por resultado do filtro novo.
  const listLoading = list.isLoading || list.isPlaceholderData;

  const columns: ColumnDef<StoredFile>[] = [
    {
      key: 'fileId',
      header: 'Arquivo',
      render: (row) => (
        <Link
          to={`/files/${row.fileId}`}
          aria-label={`Abrir arquivo ${row.fileId.slice(0, 8)}`}
          className="text-(--color-accent) font-mono text-xs hover:underline"
        >
          {row.fileId.slice(0, 8)}
        </Link>
      ),
    },
    {
      key: 'owner',
      header: 'Módulo · dono',
      render: (row) => (
        <span className="text-sm">
          {row.module} · {row.ownerType}
        </span>
      ),
    },
    { key: 'tenant', header: 'BU', render: (row) => row.tenant ?? '—' },
    {
      key: 'size',
      header: 'Tamanho',
      render: (row) => formatBytes(row.sizeBytes),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className="flex flex-wrap gap-1">
          <Badge>{STORED_FILE_STATUS_LABELS[row.status] ?? row.status}</Badge>
          {row.legalHold && <Badge variant="warning">Legal hold</Badge>}
        </span>
      ),
    },
    {
      key: 'lastResolvedAt',
      header: 'Último acesso',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.lastResolvedAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Criado em',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.createdAt)}
        </span>
      ),
    },
  ];

  const isEmpty = !listLoading && !list.isError && list.data?.total === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-(--color-text-primary) text-2xl font-semibold">
          Arquivos
        </h1>
        <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
          Todo arquivo que a plataforma guarda no bucket, de qualquer módulo. O
          nome original aparece só no detalhe.
        </p>
      </div>

      <FiltersForm
        filters={filters}
        bucketOptions={optionsFrom(options?.byBucket, filters.bucket)}
        moduleOptions={optionsFrom(options?.byModule, filters.module)}
        tenantOptions={optionsFrom(options?.byTenant, filters.tenant)}
        ownerTypeRows={options?.byOwnerType}
        onApply={setFilters}
      />

      <SummarySection
        summary={summary.data}
        isLoading={summary.isLoading}
        isStale={summary.isPlaceholderData}
        isError={summary.isError}
        onRetry={() => void summary.refetch()}
      />

      {list.isError ? (
        <ErrorState
          message="Não foi possível carregar os arquivos."
          onRetry={() => void list.refetch()}
        />
      ) : isEmpty ? (
        <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-8 text-center">
          {isDefault(filters) ? (
            <p className="text-(--color-text-secondary) text-sm">
              Nenhum arquivo ativo no registro. Os apagados e os que sumiram do
              bucket aparecem pelo filtro de status.
            </p>
          ) : (
            <>
              <p className="text-(--color-text-secondary) text-sm">
                Nenhum arquivo com esses filtros. Sem filtro, a lista mostra só
                os arquivos ativos.
              </p>
              <Button
                className="mt-4"
                variant="secondary"
                onClick={() => setFilters(DEFAULT_STORED_FILE_FILTERS)}
              >
                Limpar filtros
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
          <Table
            columns={columns}
            data={list.data?.items ?? []}
            keyExtractor={(row) => row.fileId}
            loading={listLoading}
            emptyMessage="Nenhum arquivo nesta página."
            striped
          />
          {list.data && (
            <Pagination
              page={page}
              total={list.data.total}
              perPage={PER_PAGE}
              loading={listLoading}
              label="arquivo"
              onPageChange={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FiltersForm({
  filters,
  bucketOptions,
  moduleOptions,
  tenantOptions,
  ownerTypeRows,
  onApply,
}: {
  filters: StoredFileFilters;
  bucketOptions: SelectOption[];
  moduleOptions: SelectOption[];
  tenantOptions: SelectOption[];
  ownerTypeRows: FilesOwnerTypeSummaryRow[] | undefined;
  onApply: (next: StoredFileFilters) => void;
}) {
  const [draft, setDraft] = useState(filters);
  const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvanced(filters));
  // Filtro que mudou por fora (Voltar, "Limpar filtros") reinicia o rascunho.
  // Sem `key` no form: remontar tiraria o foco do botão que aplicou.
  const filtersKey = JSON.stringify(filters);
  const [syncedKey, setSyncedKey] = useState(filtersKey);
  if (syncedKey !== filtersKey) {
    setSyncedKey(filtersKey);
    setDraft(filters);
    if (hasAdvanced(filters)) setAdvancedOpen(true);
  }
  const set =
    (key: keyof StoredFileFilters) => (e: { target: { value: string } }) =>
      setDraft((prev) => ({ ...prev, [key]: e.target.value }));
  // Trocar o módulo para outro desfaz o par escolhido: o tipo e o id eram dele.
  const setModule = (e: { target: { value: string } }) =>
    setDraft((prev) =>
      e.target.value === prev.module
        ? prev
        : { ...prev, module: e.target.value, ownerType: '', ownerId: '' },
    );
  const setOwnerType = (e: { target: { value: string } }) => {
    const row = ownerTypeRows?.find((r) => r.key === e.target.value);
    setDraft((prev) =>
      row
        ? { ...prev, module: row.module, ownerType: row.ownerType }
        : { ...prev, ownerType: '' },
    );
  };
  // O back só filtra por dono com módulo e tipo de dono juntos. O par preenche
  // os dois; sobra o id digitado sem tipo (ou uma URL montada à mão).
  const ownerIncomplete =
    (draft.ownerType.trim() !== '' || draft.ownerId.trim() !== '') &&
    (draft.module === '' || draft.ownerType.trim() === '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ownerIncomplete) onApply(draft);
  };

  return (
    <form
      onSubmit={submit}
      className="border-(--color-border) bg-(--color-bg-primary) grid gap-4 rounded-xl border p-4 sm:grid-cols-3 sm:p-6"
    >
      <Select
        id="files-filter-status"
        label="Status"
        options={STATUS_OPTIONS}
        value={draft.status}
        onChange={set('status')}
      />
      <Select
        id="files-filter-module"
        label="Módulo"
        options={moduleOptions}
        value={draft.module}
        onChange={setModule}
      />
      <Select
        id="files-filter-owner-type"
        label="Tipo de dono"
        options={ownerTypeOptionsFrom(
          ownerTypeRows,
          draft.module,
          draft.ownerType,
        )}
        value={ownerKey(draft.module, draft.ownerType)}
        onChange={setOwnerType}
      />
      <Input
        id="files-filter-owner-id"
        label="Id do dono"
        value={draft.ownerId}
        onChange={set('ownerId')}
        error={
          ownerIncomplete ? 'O id do dono pede o tipo de dono.' : undefined
        }
        helperText="Só o id da entidade, nunca e-mail ou documento: o filtro vai para a URL."
      />
      {/* `<details>` nativo, como no resto do admin-dashboard. Abre aberto
          quando um desses filtros está aplicado, para nenhum ficar escondido. */}
      <details
        className="group sm:col-span-3"
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}
      >
        <summary className="text-(--color-text-secondary) hover:text-(--color-accent) flex cursor-pointer list-none items-center gap-1 text-sm font-medium transition-colors">
          <svg
            aria-hidden="true"
            className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
          </svg>
          Mais filtros
        </summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Select
            id="files-filter-bucket"
            label="Bucket"
            options={bucketOptions}
            value={draft.bucket}
            onChange={set('bucket')}
          />
          <Select
            id="files-filter-tenant"
            label="BU"
            options={tenantOptions}
            value={draft.tenant}
            onChange={set('tenant')}
          />
          <Input
            id="files-filter-not-resolved"
            label="Sem acesso desde"
            type="date"
            value={draft.notResolvedSince}
            onChange={set('notResolvedSince')}
            helperText="Inclui os nunca acessados."
          />
          <Input
            id="files-filter-min"
            label="Tamanho mínimo (MiB)"
            type="number"
            min={0}
            step="any"
            value={draft.minSizeMib}
            onChange={set('minSizeMib')}
          />
          <Input
            id="files-filter-max"
            label="Tamanho máximo (MiB)"
            type="number"
            min={0}
            step="any"
            value={draft.maxSizeMib}
            onChange={set('maxSizeMib')}
          />
        </div>
      </details>
      <div className="flex items-end gap-2 sm:col-span-3">
        <Button type="submit" disabled={ownerIncomplete}>
          Aplicar filtros
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onApply(DEFAULT_STORED_FILE_FILTERS)}
        >
          Limpar
        </Button>
      </div>
    </form>
  );
}

function SummarySection({
  summary,
  isLoading,
  isStale,
  isError,
  onRetry,
}: {
  summary: FilesSummary | undefined;
  isLoading: boolean;
  /** Dado do filtro anterior, enquanto o novo não chega. */
  isStale: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  if (isError) {
    return (
      <ErrorState
        message="Não foi possível carregar os totais."
        onRetry={onRetry}
      />
    );
  }
  if (isLoading || !summary) {
    return (
      <div
        aria-busy="true"
        className="border-(--color-border) bg-(--color-bg-primary) h-40 animate-pulse rounded-xl border"
      />
    );
  }
  if (summary.byBucket.length === 0) {
    return (
      <p className="text-(--color-text-secondary) text-sm">
        Nenhum arquivo para somar com esses filtros.
      </p>
    );
  }
  return (
    <section
      aria-label="Totais"
      aria-busy={isStale}
      className={`grid gap-4 md:grid-cols-3 ${isStale ? 'opacity-60' : ''}`}
    >
      <SummaryCard title="Por bucket" rows={summary.byBucket} />
      <SummaryCard title="Por módulo" rows={summary.byModule} />
      <SummaryCard title="Por BU" rows={summary.byTenant} />
    </section>
  );
}

function SummaryCard({
  title,
  rows,
}: {
  title: string;
  rows: FilesSummaryRow[];
}) {
  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-4">
      <h2 className="text-(--color-text-primary) text-sm font-semibold">
        {title}
      </h2>
      <ul className="mt-3 space-y-2">
        {rows.map((row) => (
          <li
            key={row.key ?? '—'}
            className="flex justify-between gap-2 text-sm"
          >
            <span className="text-(--color-text-primary)">{row.label}</span>
            <span className="text-(--color-text-secondary) text-right">
              {formatBytes(row.totalBytes)} · {row.count}{' '}
              {row.count === 1 ? 'arquivo' : 'arquivos'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
